/**
 * CPU-offload public surface + the gated call wrappers.
 *
 * **Disabled by default.** The host opts in via {@link configureCpuOffload}:
 * apps/api wires it to a knob; the CLI never configures it, so it stays off.
 * When disabled every wrapper runs the work inline — identical to today's
 * behavior — so flipping the gate is the only thing that changes execution.
 *
 * @module cpu-offload
 */

import { cpus } from 'node:os';
import { type BrowserLogger, consoleLogger } from '../logger.js';
import { withExpensiveSlot } from '../expensive-gate.js';
import { recordInlineFallback } from '../worker-pool/inline-fallback.js';
import { formatSemanticSnapshot } from '../snapshot-formatter-render.js';
import {
  runImageOp,
  type ImageOpOutcome,
  type ImageOpRequest,
  type ImageOpResult,
} from '../image-ops.js';
import type { SemanticSnapshotOptions } from '../snapshot-formatter-shared.js';
import {
  CpuOffloadOomError,
  CpuOffloadPool,
  type CpuOffloadPoolStats,
  CpuOffloadTimeoutError,
} from './pool.js';
import type { FormatSnapshotPayload, FormatSnapshotResult } from './tasks.js';
import { applyAugmentTail, type AugmentTailPayload, type AugmentTailResult } from './tasks.js';

export {
  CpuOffloadPool,
  CpuOffloadOomError,
  CpuOffloadTimeoutError,
  CpuOffloadWorkerError,
  resolveCpuOffloadWorkerPath,
} from './pool.js';
export type { CpuOffloadPoolOptions, CpuOffloadPoolStats } from './pool.js';
export {
  DEFAULT_URL_DOWNLOAD_RSS_BYTES,
  DEFAULT_URL_DOWNLOAD_TIMEOUT_MS,
  downloadUrlToPathIsolated,
  type IsolatedUrlDownload,
} from './download-url-to-path.js';
export {
  DEFAULT_TRACE_MERGE_RSS_BYTES,
  DEFAULT_TRACE_MERGE_TIMEOUT_MS,
  mergeTraceArchivesIsolated,
} from './trace-merge.js';
export {
  DEFAULT_TRACE_INDEX_RSS_BYTES,
  DEFAULT_TRACE_INDEX_TIMEOUT_MS,
  indexTraceArchiveIsolated,
  type IsolatedTraceArchiveIndex,
} from './trace-index.js';

/** Shared defaults — exported so the API knob layer can't drift from them. */
export const DEFAULT_CPU_OFFLOAD_TIMEOUT_MS = 15_000;

/**
 * Default worker count: ~half the vCPUs, bounded to [2, 8].
 *
 * ⚠️ `os.cpus()` reports the HOST's core count, not the container's CPU limit,
 * so under ECS this OVER-counts on a packed instance. The cap is only a
 * backstop — set the `browser.cpu-offload.size` knob per env to match the
 * task's actual vCPU rather than relying on this default.
 */
export const defaultCpuOffloadSize = (): number =>
  Math.min(8, Math.max(2, Math.floor(cpus().length / 2)));

export interface CpuOffloadConfig {
  /**
   * Master gate. `boolean` for a static setting, or a function so the host can
   * back it with a live knob (re-read per call — cheap).
   */
  enabled: boolean | (() => boolean);
  /**
   * Sub-gate for the Jimp image operations (`browser.cpu-offload.images`).
   * Default off: image work runs inline exactly as before. Requires `enabled`
   * too — the pool has to exist for anything to offload to. Separate from
   * `enabled` because that knob is already on in prod, so this is what lets
   * image routing ramp (and revert) on its own. `boolean` or a live function
   * gate, same as `enabled`.
   */
  images?: boolean | (() => boolean);
  /**
   * Worker count. Defaults to ~half the vCPUs. NOTE: applied when the pool is
   * first created; changing it later takes effect only after the pool is torn
   * down (e.g. an enabled→disabled→enabled toggle).
   */
  size?: number;
  /** Default per-call SIGKILL budget (execution time; see pool docs). */
  timeoutMs?: number;
  /**
   * Per-worker RSS cap in bytes. Unset ⇒ the RSS monitor never runs (today's
   * behavior). Applied when the pool is first created (like `size`).
   */
  rssBytes?: number;
  /** RSS-monitor cadence in ms (defaults to the pool's 1 Hz). */
  rssCheckIntervalMs?: number;
  /**
   * Arm the per-driver CPU/PSS calibration sampler at this cadence (ms). Unset/`0`
   * ⇒ off (default). Applied **live** via {@link configureCpuOffload} — flipping it
   * starts/stops sampling on an already-warm pool, no teardown needed.
   */
  measureResourcesMs?: number;
  logger?: BrowserLogger;
}

let config: CpuOffloadConfig = { enabled: false };
let pool: CpuOffloadPool | null = null;

/**
 * Configure (and enable/disable) CPU offload. Safe to call repeatedly — e.g. on
 * knob change. Passing a literal `enabled: false` tears down the worker pool;
 * the API passes a function gate, so it owns teardown via shutdownCpuOffload.
 */
export function configureCpuOffload(next: CpuOffloadConfig): void {
  config = next;
  if (typeof next.enabled === 'boolean' && next.enabled === false) {
    void shutdownCpuOffload();
    return;
  }
  // Apply the calibration cadence live so flipping the knob takes effect on an
  // already-warm pool (idempotent when unchanged; no-op until the pool is built).
  pool?.setMeasureResourcesMs(config.measureResourcesMs ?? 0);
}

export function isCpuOffloadEnabled(): boolean {
  const { enabled } = config;
  return typeof enabled === 'function' ? !!enabled() : enabled;
}

/**
 * Whether Jimp image operations route through the pool. Both gates must be on:
 * `images` is a sub-gate of `enabled`, not an independent switch.
 */
export function isCpuOffloadImagesEnabled(): boolean {
  const { images } = config;
  if (!isCpuOffloadEnabled()) return false;
  return typeof images === 'function' ? !!images() : !!images;
}

function getPool(): CpuOffloadPool {
  if (!pool) {
    pool = new CpuOffloadPool({
      size: config.size ?? defaultCpuOffloadSize(),
      defaultTimeoutMs: config.timeoutMs ?? DEFAULT_CPU_OFFLOAD_TIMEOUT_MS,
      ...(config.rssBytes ? { rssBytes: config.rssBytes } : {}),
      ...(config.rssCheckIntervalMs ? { rssCheckIntervalMs: config.rssCheckIntervalMs } : {}),
      ...(config.measureResourcesMs ? { measureResourcesMs: config.measureResourcesMs } : {}),
      logger: config.logger ?? consoleLogger,
    });
  }
  return pool;
}

export async function shutdownCpuOffload(): Promise<void> {
  const p = pool;
  pool = null;
  if (p) await p.shutdown();
}

/**
 * Current pool stats (occupancy + the kill/RSS counters), or `null` when
 * the pool isn't built — i.e. offload disabled / torn down. The host's telemetry
 * loop reads this each interval and emits per-driver metrics; a `null` return is
 * the zero-cost no-op path when the feature is off.
 */
export function getCpuOffloadStats(): CpuOffloadPoolStats | null {
  return pool ? pool.stats() : null;
}

/** Default budget for the boot smoke-test round-trip (spawn + handshake + ping). */
const SMOKE_TEST_TIMEOUT_MS = 5_000;

/**
 * Boot smoke-test: spawn one worker and run a trivial task (`__ping`) end-to-end.
 *
 * This proves a child can actually SPAWN, complete the READY handshake, and
 * exchange a framed message — unlike a path `existsSync` probe, which only proves
 * the file is present, not that the process can start. A packaging break (e.g. an
 * `EACCES` reading workspace `./src/*.ts`) only manifests when the process really
 * starts, so this is the check that catches it. Throws on any failure
 * (spawn/handshake/timeout) so the host can log a loud boot failure instead of
 * silently falling back inline per snapshot. The spawned worker stays warm in the
 * idle pool on success (on failure the pool is left instantiated but empty; the
 * caller's inline fallback still works).
 *
 * Call this AFTER {@link configureCpuOffload} so the warm pool is built with the
 * configured size/timeout — a no-config call builds the singleton with defaults,
 * and a live pool is not rebuilt by a later configure.
 */
export async function smokeTestCpuOffload(
  timeoutMs: number = SMOKE_TEST_TIMEOUT_MS
): Promise<void> {
  await getPool().run('__ping', {}, { timeoutMs });
}

/**
 * Probe that the pool can be built, *before* the caller queues for a gate slot: if
 * its worker entry can't resolve, no call is ever poolable, and that systemic
 * failure carries its own reason rather than masquerading as a per-call fault — and
 * shouldn't consume shared capacity to discover. `false` means run inline; the
 * fallback is already counted and logged here. Callers still resolve the pool late
 * (inside the gate closure) so a teardown mid-wait transparently rebuilds.
 */
function ensurePoolBuildable(task: string, logger: BrowserLogger): boolean {
  try {
    getPool();
    return true;
  } catch (err) {
    recordInlineFallback('cpu-offload', 'pool_unavailable');
    logger.warn(`[CpuOffload] pool unavailable; running ${task} inline`, {
      task,
      error: err instanceof Error ? err.message : String(err),
    });
    return false;
  }
}

/**
 * Why an offloaded task failed. `oom`/`timeout` are the child's own ceilings
 * firing — containment working — while anything else is a worker/pool fault.
 */
function classifyOffloadFailure(err: unknown): 'oom' | 'timeout' | 'error' {
  if (err instanceof CpuOffloadOomError) return 'oom';
  if (err instanceof CpuOffloadTimeoutError) return 'timeout';
  return 'error';
}

/** @internal test seam — also reclaims any live worker processes. */
export function _resetCpuOffloadForTest(): void {
  const p = pool;
  config = { enabled: false };
  pool = null;
  if (p) void p.shutdown();
}

/**
 * Marker returned when an enabled offload TIMES OUT. We deliberately do NOT
 * re-run the formatter inline on timeout: a timeout means the input was
 * pathological, and re-running it on the host event loop is exactly the wedge
 * the offload exists to prevent. The agent gets an actionable signal instead.
 */
const TIMEOUT_MARKER =
  '---\nSEMANTIC SNAPSHOT - formatting timed out\n' +
  'The page was too large to format within the time budget. ' +
  'Narrow with browser_snapshot search=… or scroll to a region.\n---';

/**
 * Format a captured substrate into the semantic snapshot string, offloaded to a
 * worker process when enabled.
 *
 * Fallback policy:
 * - **disabled / pool unavailable / worker crash** → run inline (status quo;
 *   the work did not time out, so the host can safely run it).
 * - **timeout** → return {@link TIMEOUT_MARKER}, never inline (see above).
 *
 * Each inline fallback taken for a *fault* is counted
 * ({@link recordInlineFallback}) — the fallback runs outside the shared expensive
 * gate, so a systemic worker fault would otherwise unwind the convergence bound
 * silently. The disabled path is configured behavior and is not counted, and
 * neither is the timeout/OOM path (it returns the marker rather than inlining).
 *
 * `elementHints` (a `Map`) and the `matchedHintIds` out-param are threaded
 * across the process boundary explicitly so offloaded output matches inline.
 */
export async function formatSnapshotMaybeOffloaded(
  yaml: string,
  options?: SemanticSnapshotOptions
): Promise<string> {
  if (!isCpuOffloadEnabled()) {
    return formatSemanticSnapshot(yaml, options);
  }
  const logger = config.logger ?? consoleLogger;
  if (!ensurePoolBuildable('format-snapshot', logger)) {
    return formatSemanticSnapshot(yaml, options);
  }
  try {
    const { elementHints, matchedHintIds: _drop, ...rest } = options ?? {};
    const payload: FormatSnapshotPayload = {
      yaml,
      options: rest,
      ...(elementHints ? { elementHints: [...elementHints] } : {}),
    };
    const result = await withExpensiveSlot(() =>
      getPool().run<FormatSnapshotResult>('format-snapshot', payload)
    );
    // Re-attach the out-param the inline path mutates, so callers that read
    // `options.matchedHintIds` after the call (e.g. HintProvider.recordMatches)
    // keep working under offload.
    if (options && result.matchedHintIds) {
      options.matchedHintIds = result.matchedHintIds;
    }
    return result.text;
  } catch (err) {
    // Timeout AND OOM both mean the input was pathological (too slow / too large);
    // re-running it inline is exactly the wedge — a CPU peg or a host OOM — the
    // offload exists to prevent. Return the marker, never inline. No fallback is
    // counted: nothing ran inline, and containment did its job.
    const reason = classifyOffloadFailure(err);
    if (reason !== 'error') {
      logger.warn('[CpuOffload] format-snapshot terminated; returning marker (NOT inlining)', {
        task: 'format-snapshot',
        reason,
        error: err instanceof Error ? err.message : String(err),
      });
      return TIMEOUT_MARKER;
    }
    recordInlineFallback('cpu-offload', 'runner_fault');
    logger.warn('[CpuOffload] format-snapshot offload failed; running inline', {
      task: 'format-snapshot',
      error: err instanceof Error ? err.message : String(err),
    });
    return formatSemanticSnapshot(yaml, options);
  }
}

/**
 * Apply the pure tail of substrate augmentation, offloaded to a worker process
 * when enabled. Unlike formatting, the tail is a set of bounded O(n) string
 * passes with no pathological blowup, so a timeout/crash safely falls back to
 * running inline (it can't wedge the host the way a giant format render can).
 *
 * Because this wrapper inlines on a *breach* too, its fallbacks are counted under
 * two different reasons: `limit_breach` when containment worked as designed, and
 * `runner_fault` when the worker itself failed. Only the latter says the pool is
 * unhealthy, so they must not share one series.
 */
export async function augmentTailMaybeOffloaded(payload: AugmentTailPayload): Promise<string> {
  if (!isCpuOffloadEnabled()) {
    return applyAugmentTail(payload).yaml;
  }
  const logger = config.logger ?? consoleLogger;
  if (!ensurePoolBuildable('augment-tail', logger)) {
    return applyAugmentTail(payload).yaml;
  }
  try {
    const result = await withExpensiveSlot(() =>
      getPool().run<AugmentTailResult>('augment-tail', payload)
    );
    return result.yaml;
  } catch (err) {
    // The tail is bounded O(n) string work, so unlike format-snapshot an OOM/timeout
    // here can safely fall back inline — but surface `reason` so an OOM still shows up
    // in queries (the child hit its RSS cap even on this bounded pass).
    const reason = classifyOffloadFailure(err);
    recordInlineFallback('cpu-offload', reason === 'error' ? 'runner_fault' : 'limit_breach');
    logger.warn('[CpuOffload] augment-tail offload failed; running inline', {
      task: 'augment-tail',
      reason,
      error: err instanceof Error ? err.message : String(err),
    });
    return applyAugmentTail(payload).yaml;
  }
}

/**
 * Run one pure Jimp image operation, offloaded to a worker process when the
 * `images` sub-gate is on (spec §13-T3).
 *
 * Image work is `async` in signature but synchronous in fact — Jimp is pure JS,
 * so decode/resize/encode and the per-pixel scans block the calling thread for
 * their whole duration (20–830 ms measured across viewport-to-full-page
 * screenshots, unbounded for the AI filesystem tool's arbitrary on-disk images).
 * Offloading moves that off the supervisor loop and puts it under the child's
 * wall-clock timeout and RSS cap; the shared expensive slot makes it count
 * against the convergence bound like every other expensive draw.
 *
 * Fallback policy mirrors {@link formatSnapshotMaybeOffloaded}:
 * - **gate off / pool unavailable / worker crash** → run inline (status quo).
 * - **timeout or OOM** → rethrow, never inline. The breach means the image was
 *   pathological, and re-running it on the host is precisely the wedge the
 *   offload exists to prevent. Callers already degrade: the executor's grid and
 *   annotate passes keep the un-decorated screenshot, the browser-stream client
 *   keeps the original, and frame extraction surfaces a typed Effect failure.
 * - **the operation's own failure** (an undecodable image) → rethrow, never
 *   inline. It is deterministic, so re-running it would fail identically after
 *   another full decode; it is also not a pool fault, so it must not be counted
 *   as one (see {@link ImageOpOutcome}).
 *
 * Fault fallbacks are counted ({@link recordInlineFallback}) because they run
 * outside the gate — a systemic worker fault would otherwise unwind the bound
 * silently. The gate-off path is configured behavior and is not counted, and
 * neither is a breach (nothing ran inline).
 *
 * **Note the divergence from `formatSnapshotMaybeOffloaded`:** that wrapper
 * absorbs a breach and returns a caller-facing marker string, because a snapshot
 * has a meaningful degraded form. An image does not — a truncated or absent
 * screenshot is not a screenshot — so this one *throws* on the paths above. Every
 * current caller tolerates it (see the list above); a new call site must handle
 * the rejection rather than assume marker semantics.
 */
export async function runImageOpMaybeOffloaded(request: ImageOpRequest): Promise<ImageOpResult> {
  if (!isCpuOffloadImagesEnabled()) {
    return runImageOp(request);
  }
  const logger = config.logger ?? consoleLogger;
  if (!ensurePoolBuildable('image-op', logger)) {
    return runImageOp(request);
  }
  let outcome: ImageOpOutcome;
  try {
    outcome = await withExpensiveSlot(() => getPool().run<ImageOpOutcome>('image-op', request));
  } catch (err) {
    const reason = classifyOffloadFailure(err);
    if (reason !== 'error') {
      logger.warn('[CpuOffload] image-op terminated; rethrowing (NOT inlining)', {
        task: 'image-op',
        op: request.op,
        reason,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
    recordInlineFallback('cpu-offload', 'runner_fault');
    logger.warn('[CpuOffload] image-op offload failed; running inline', {
      task: 'image-op',
      op: request.op,
      error: err instanceof Error ? err.message : String(err),
    });
    return runImageOp(request);
  }
  // Deliberately outside the try: the image's own failure must surface as the
  // inline path would, and throwing it *inside* would land in the catch above
  // and be miscounted as a pool fault (plus burn a second decode on the host).
  if (!outcome.ok) {
    const err = new Error(outcome.message);
    if (outcome.name) err.name = outcome.name;
    throw err;
  }
  return { base64: outcome.base64 };
}
